/**
 * RecentSection — Recently completed/failed missions.
 * Visual style matches admin-ui sidebar sections.
 */

import React from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Badge, Button, Text, cn } from '@agion/ui'
import { useExecutionStore } from '../../hooks/useExecutionStore'
import { useConversationHistory, formatExecutionTitle } from '../../hooks/useConversationHistory'
import { useMissionRunHistory } from '../../hooks/useMissionRunHistory'
import type { MissionConversationGroup } from '../../types/chat'

const MAX_RECENT = 10
/** Runs listed under an expanded mission before its "show all" control. */
const MAX_EXECUTIONS = 5

const runRowClass = cn(
  'w-full flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm transition-colors h-auto text-left',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1',
)
const controlClass = cn(runRowClass, 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/30')

function formatRelativeTime(date: Date, t: ReturnType<typeof useTranslation<'chat'>>['t']): string {
  const diffMs = Date.now() - date.getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMin < 1) return t('timeAgo.justNow' as Parameters<typeof t>[0])
  if (diffHours < 1) return t('timeAgo.minutesAgo' as Parameters<typeof t>[0], { count: diffMin })
  if (diffDays < 1) return t('timeAgo.hoursAgo' as Parameters<typeof t>[0], { count: diffHours })
  return t('timeAgo.daysAgo' as Parameters<typeof t>[0], { count: diffDays })
}

/**
 * An expanded mission's runs: its newest five from the history, then a
 * "show all" control that lists every run of the mission, newest first,
 * read by mission_id in pages (agion/Agion#2874).
 */
function RecentMissionRuns({
  group,
  isActive,
  urlExecutionId,
}: {
  group: MissionConversationGroup
  isActive: boolean
  urlExecutionId: string | null
}) {
  const { t } = useTranslation('chat')
  const navigate = useNavigate()
  const [showAll, setShowAll] = React.useState(false)
  const all = useMissionRunHistory(group.missionId, showAll)
  const canShowAll = group.executions.length > MAX_EXECUTIONS
  const runs = showAll && all.loaded ? all.entries : group.executions.slice(0, MAX_EXECUTIONS)

  return (
    <div className="ml-6 mt-1 space-y-0.5" data-testid="recent-executions">
      {runs.map((exec) => {
        const isSelectedExecution = isActive && urlExecutionId === exec.id

        return (
          <Button
            key={exec.id}
            variant="ghost"
            onClick={() => navigate(`/mission/${group.missionId}?execution=${exec.id}`)}
            className={cn(
              runRowClass,
              isSelectedExecution
                ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-200'
                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/30'
            )}
            data-testid="recent-execution-row"
          >
            <span className={cn(
              'w-1.5 h-1.5 rounded-full flex-shrink-0',
              isSelectedExecution
                ? 'bg-amber-500 dark:bg-amber-400'
                : 'bg-slate-300 dark:bg-slate-600'
            )} />
            <span className="flex-1 truncate text-xs">
              {formatExecutionTitle(exec.lastActivityAt)}
            </span>
          </Button>
        )
      })}

      {canShowAll && !showAll && (
        <Button variant="ghost" onClick={() => setShowAll(true)} className={controlClass} data-testid="recent-show-all">
          <span className="flex-1 truncate text-xs">{t('recentRuns.showAll', 'Show all chats')}</span>
        </Button>
      )}

      {showAll && (
        <>
          {all.isLoading && (
            <Text as="p" size="xs" color="muted" className="px-3 py-1.5" role="status">
              {t('recentRuns.loading', 'Loading chats…')}
            </Text>
          )}
          {!!all.failure && !all.isLoading && (
            <div className="flex items-center gap-2 px-3 py-1.5" data-testid="recent-runs-load-error">
              <Text as="span" size="xs" color="muted" className="flex-1">
                {t('recentRuns.loadError', 'Earlier chats could not be loaded')}
              </Text>
              <Button variant="ghost" size="sm" onClick={all.retry}>
                {t('common:retry', 'Retry')}
              </Button>
            </div>
          )}
          {all.hasMore && !all.isLoading && !all.failure && (
            <Button variant="ghost" onClick={all.loadMore} className={controlClass} data-testid="recent-show-more">
              <span className="flex-1 truncate text-xs">{t('recentRuns.showMore', 'Show more chats')}</span>
            </Button>
          )}
          <Button variant="ghost" onClick={() => setShowAll(false)} className={controlClass} data-testid="recent-show-fewer">
            <span className="flex-1 truncate text-xs">{t('recentRuns.showFewer', 'Show fewer')}</span>
          </Button>
        </>
      )}
    </div>
  )
}

export function RecentSection({ searchTerm = '' }: { searchTerm?: string }) {
  const { t } = useTranslation('chat')
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const missionStates = useExecutionStore((s) => s.missionStates)
  const { groupedByMission } = useConversationHistory()
  const [expandedMissions, setExpandedMissions] = React.useState<Set<string>>(new Set())

  const urlMissionId = location.pathname.startsWith('/mission/')
    ? location.pathname.split('/mission/')[1]?.split('/')[0]
    : null
  const urlExecutionId = searchParams.get('execution')

  // Auto-expand mission when a specific execution is selected via URL
  React.useEffect(() => {
    if (urlMissionId && urlExecutionId) {
      setExpandedMissions((prev) => {
        if (prev.has(urlMissionId)) return prev
        const next = new Set(prev)
        next.add(urlMissionId)
        return next
      })
    }
  }, [urlMissionId, urlExecutionId])

  const recentGroups = React.useMemo(() => {
    const term = searchTerm.toLowerCase()
    return groupedByMission
      .filter((group) => {
        const state = missionStates.get(group.missionId)
        // Exclude missions shown in "Needs Your Response" section
        if (state?.hasActiveHITL) return false
        if (term && !group.missionName?.toLowerCase().includes(term)) return false
        return true
      })
      .slice(0, MAX_RECENT)
  }, [groupedByMission, missionStates, searchTerm])

  if (recentGroups.length === 0) return null

  const toggleMission = (missionId: string) => {
    setExpandedMissions((prev) => {
      const next = new Set(prev)
      if (next.has(missionId)) {
        next.delete(missionId)
      } else {
        next.add(missionId)
      }
      return next
    })
  }

  return (
    <div className="mb-3">
      {/* Section header — premium sidebar pattern */}
      <div className="flex items-center gap-2 px-1 py-2">
        <span className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider flex-1">
          {t('recent', 'Recent')}
        </span>
        <Badge variant="default" size="sm">{recentGroups.length}</Badge>
      </div>

      <AnimatePresence>
        <div className="space-y-0.5">
          {recentGroups.map((group) => {
            const isExpanded = expandedMissions.has(group.missionId)
            const hasMultiple = group.executions.length > 1
            const isActive = urlMissionId === group.missionId

            return (
              <motion.div
                key={group.missionId}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
              >
                <Button
                  variant="ghost"
                  onClick={() =>
                    hasMultiple
                      ? toggleMission(group.missionId)
                      : navigate(`/mission/${group.missionId}`)
                  }
                  className={cn(
                    'w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors h-auto text-left',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1',
                    isActive
                      ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-900 dark:text-slate-100'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/30'
                  )}
                >
                  {/* Chevron or spacer */}
                  <span className="w-5 flex items-center justify-center flex-shrink-0">
                    {hasMultiple ? (
                      isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                      )
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600" />
                    )}
                  </span>

                  <Text size="sm" weight="medium" className="truncate flex-1">
                    {group.missionName}
                  </Text>

                  <Text size="xs" color="muted" className="flex-shrink-0 tabular-nums">
                    {formatRelativeTime(group.latestActivityAt, t)}
                  </Text>
                </Button>

                {/* Expanded executions — indented children */}
                {hasMultiple && isExpanded && (
                  <RecentMissionRuns
                    group={group}
                    isActive={isActive}
                    urlExecutionId={urlExecutionId}
                  />
                )}
              </motion.div>
            )
          })}
        </div>
      </AnimatePresence>
    </div>
  )
}
